/**
 * Component: sa-job-detail
 * Description: Job detail page with application form
 */
if (!customElements.get('sa-job-detail')) {
  class SaJobDetail extends HTMLElement {
    constructor() {
      super();
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleFileSelect = this.handleFileSelect.bind(this);
      this.handleDragOver = this.handleDragOver.bind(this);
      this.handleDrop = this.handleDrop.bind(this);
      this.handleRecruitmentClick = this.handleRecruitmentClick.bind(this);
      // filesArray will now store objects: { file: File, status: 'pending'|'uploading'|'success'|'error', id: string|null, error: string|null }
      this.filesArray = [];
    }

    connectedCallback() {
      // 0. Get Shop Domain
      this.shopDomain = this.getAttribute('data-shop-domain');

      // 1. Form handling
      this.form = this.querySelector('#sa-application-form');
      this.submitBtn = this.querySelector('.sa-job-detail__submit-btn');
      
      if (this.submitBtn) {
        this.originalSubmitBtnContent = this.submitBtn.innerHTML;
      }

      if (this.form) {
        this.form.addEventListener('submit', this.handleSubmit);
      }

      // 1.5 Recruitment Button Handling
      this.recruitmentBtns = this.querySelectorAll('.sa-job-detail__recruitment-btn');
      this.recruitmentInput = this.querySelector('#sa-recruitment-input');
      
      if (this.recruitmentBtns.length > 0) {
        this.recruitmentBtns.forEach(btn => {
          btn.addEventListener('click', this.handleRecruitmentClick);
        });
      }

      // 2. File upload handling
      this.fileInput = this.querySelector('#sa-resume-input');
      this.fileArea = this.querySelector('#sa-file-upload-area');
      this.chooseBtn = this.querySelector('#sa-choose-file-btn');
      this.fileList = this.querySelector('#sa-file-list');
      
      if (this.chooseBtn && this.fileInput) {
        this.chooseBtn.addEventListener('click', () => this.fileInput.click());
        this.fileInput.addEventListener('change', this.handleFileSelect);
      }

      if (this.fileList) {
        this.fileList.addEventListener('click', (e) => {
          if (e.target.closest('.sa-job-detail__remove-file')) {
             const btn = e.target.closest('.sa-job-detail__remove-file');
             const index = parseInt(btn.dataset.index);
             this.handleRemoveFile(index);
          }
        });
      }

      if (this.fileArea) {
        this.fileArea.addEventListener('dragover', this.handleDragOver);
        this.fileArea.addEventListener('drop', this.handleDrop);
      }

      // 3. Success Message
      this.successMessage = this.querySelector('#sa-success-message');
      this.errorMessage = this.querySelector('#sa-error-message');

      // 4. Real-time validation: Clear error state on input interaction
      this.querySelectorAll('input, select, textarea').forEach(input => {
        ['input', 'change'].forEach(evt => {
          input.addEventListener(evt, () => {
            const wrapper = input.closest('.sa-job-detail__form-group');
            if (wrapper) wrapper.classList.remove('sa-job-detail__form-group--error');
          });
        });
      });
    }

    disconnectedCallback() {
      if (this.form) {
        this.form.removeEventListener('submit', this.handleSubmit);
      }
      if (this.chooseBtn) {
        this.chooseBtn.removeEventListener('click', () => this.fileInput.click());
      }
      if (this.fileInput) {
        this.fileInput.removeEventListener('change', this.handleFileSelect);
      }
      if (this.fileArea) {
        this.fileArea.removeEventListener('dragover', this.handleDragOver);
        this.fileArea.removeEventListener('drop', this.handleDrop);
      }
      if (this.recruitmentBtns) {
        this.recruitmentBtns.forEach(btn => {
          btn.removeEventListener('click', this.handleRecruitmentClick);
        });
      }
    }

    handleRecruitmentClick(e) {
      e.preventDefault();
      const btn = e.currentTarget;
      const value = btn.dataset.value;

      // Toggle selection state
      if (btn.classList.contains('is-selected')) {
        btn.classList.remove('is-selected');
      } else {
        btn.classList.add('is-selected');
      }

      // Collect selected values
      const selectedValues = [];
      this.recruitmentBtns.forEach(b => {
        if (b.classList.contains('is-selected')) {
          selectedValues.push(b.dataset.value);
        }
      });

      // Update hidden input with comma-separated values
      if (this.recruitmentInput) {
        this.recruitmentInput.value = selectedValues.join(', ');
        // Trigger change event to clear error if any
        this.recruitmentInput.dispatchEvent(new Event('change'));
      }
    }

    handleDragOver(e) {
      e.preventDefault();
      e.stopPropagation();
      this.fileArea.classList.add('sa-job-detail__file-upload--drag');
    }

    handleDrop(e) {
      e.preventDefault();
      e.stopPropagation();
      this.fileArea.classList.remove('sa-job-detail__file-upload--drag');
      
      const files = Array.from(e.dataTransfer.files);
      this.addFiles(files);
    }

    handleFileSelect(e) {
      const files = Array.from(e.target.files);
      this.addFiles(files);
      // Do not clear input value here, as it would clear the files we just set in addFiles via updateFileInput.
      // We rely on the user selecting different files or the browser handling re-selection.
    }

    updateSubmitButtonState() {
      if (!this.submitBtn) return;
      
      // If we are already in submitting state (via handleSubmit), do not interfere
      if (this.submitBtn.classList.contains('loading')) return;

      const isUploading = this.filesArray.some(f => f.status === 'uploading' || f.status === 'pending');
      
      if (isUploading) {
        this.submitBtn.disabled = true;
        this.submitBtn.textContent = 'Loading...';
        this.submitBtn.style.opacity = '0.7';
      } else {
        this.submitBtn.disabled = false;
        // Restore original content if not stored yet (though we store in connectedCallback)
        if (this.originalSubmitBtnContent) {
           this.submitBtn.innerHTML = this.originalSubmitBtnContent;
        } else {
           this.submitBtn.textContent = 'Submit';
        }
        this.submitBtn.style.opacity = '1';
      }
    }

    addFiles(files) {
      if (files.length === 0) return;
      
      // Check current count + new files count
      if (this.filesArray.length + files.length > 5) {
        this.showErrorMessage('You can only upload up to 5 files.');
        return;
      }

      const newFiles = [];
      const allowedExtensions = ['.pdf', '.png', '.jpg', '.jpeg'];

      files.forEach(file => {
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        
        if (file.size > 7 * 1024 * 1024) {
          this.showErrorMessage(`File "${file.name}" exceeds 7MB limit.`);
        } else if (!allowedExtensions.includes(fileExtension)) {
          this.showErrorMessage(`File "${file.name}" type is not allowed. Allowed types: .pdf, .png, .jpg, .jpeg`);
        } else {
          newFiles.push({
            file: file,
            status: 'pending',
            id: null,
            error: null
          });
        }
      });

      if (newFiles.length > 0) {
        const startIndex = this.filesArray.length;
        this.filesArray = [...this.filesArray, ...newFiles];
        
        this.updateFileInput();
        this.renderFileList();
        
        // Update button state immediately
        this.updateSubmitButtonState();
        
        const wrapper = this.fileInput.closest('.sa-job-detail__form-group');
        if (wrapper) wrapper.classList.remove('sa-job-detail__form-group--error');

        newFiles.forEach((fileObj, i) => {
          this.uploadSingleFile(startIndex + i);
        });
      }
    }

    showErrorMessage(message) {
      if (this.errorMessage) {
        this.errorMessage.textContent = message;
        this.errorMessage.style.display = 'block';
        
        // Hide after 5 seconds
        if (this.errorTimeout) clearTimeout(this.errorTimeout);
        this.errorTimeout = setTimeout(() => {
          this.errorMessage.style.display = 'none';
        }, 5000);
      } else {
        // Fallback if element not found
        alert(message);
      }
    }

    async uploadSingleFile(index) {
      const fileObj = this.filesArray[index];
      if (!fileObj || fileObj.status !== 'pending') return;

      fileObj.status = 'uploading';
      this.renderFileList(); 
      this.updateSubmitButtonState(); // Update state to Loading...

      const formData = new FormData();
      formData.append('file', fileObj.file);

      try {
        const response = await fetch(`/apps/sharpa-careers/upload?shop=${this.shopDomain}`, {
          method: "POST",
          body: formData
        });
        
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || error.error || 'Upload failed');
        }
        
        const result = await response.json();
        fileObj.status = 'success';
        fileObj.id = result.fileId;
      } catch (err) {
        console.error('Upload error:', err);
        fileObj.status = 'error';
        fileObj.error = err.message;
      }

      this.renderFileList();
      this.updateSubmitButtonState(); // Check if all done, restore button
    }

    handleRemoveFile(index) {
      this.filesArray.splice(index, 1);
      this.updateFileInput();
      this.renderFileList();
      this.updateSubmitButtonState(); // Re-check state after removal
    }

    updateFileInput() {
      const dt = new DataTransfer();
      this.filesArray.forEach(fileObj => dt.items.add(fileObj.file));
      this.fileInput.files = dt.files;
    }

    renderFileList() {
      if (!this.fileList) return;
      this.fileList.innerHTML = '';
      
      this.filesArray.forEach((fileObj, index) => {
        const item = document.createElement('div');
        item.className = 'sa-job-detail__file-item';
        
        let statusIcon = '';
        
        if (fileObj.status === 'uploading') {
          statusIcon = '<span class="sa-job-detail__spinner">↻</span>';
        } else if (fileObj.status === 'success') {
          statusIcon = '<span style="color: green;">✓</span>';
        } else if (fileObj.status === 'error') {
          statusIcon = '<span style="color: red;">⚠</span>';
        }

        item.innerHTML = `
          <div class="sa-job-detail__file-info">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="sa-job-detail__file-icon">
              <path d="M11.3327 3.33301H5.99935C5.64573 3.33301 5.30659 3.47348 5.05654 3.72353C4.80649 3.97358 4.66602 4.31272 4.66602 4.66634V15.333C4.66602 15.6866 4.80649 16.0258 5.05654 16.2758C5.30659 16.5259 5.64573 16.6663 5.99935 16.6663H13.9993C14.353 16.6663 14.6921 16.5259 14.9422 16.2758C15.1922 16.0258 15.3327 15.6866 15.3327 15.333V7.33301L11.3327 3.33301Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M11.334 3.33301V7.33301H15.334" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M12.6673 10.667H7.33398" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M12.6673 13.333H7.33398" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M8.66732 8H8.00065H7.33398" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span class="sa-job-detail__file-name">${fileObj.file.name}</span>
            <span class="sa-job-detail__file-status">${statusIcon}</span>
          </div>
        `;
        
        const removeBtn = document.createElement('div');
        removeBtn.className = 'sa-job-detail__remove-file';
        removeBtn.dataset.index = index;
        removeBtn.innerHTML = '×';
        removeBtn.style.cursor = 'pointer';
        removeBtn.style.marginLeft = 'auto';
        removeBtn.style.padding = '0 5px';
        
        item.querySelector('.sa-job-detail__file-info').appendChild(removeBtn);
        
        this.fileList.appendChild(item);
      });
    }

    validateForm() {
      let isValid = true;
      let firstInvalid = null;

      const fields = this.querySelectorAll('.sa-job-detail__form-group');
      
      fields.forEach(field => {
        const input = field.querySelector('input[required], select[required]');
        
        // Check if this is the file upload group (resume)
        const isFileGroup = field.querySelector('#sa-resume-input');
        // Check if this is the recruitment group
        const isRecruitmentGroup = field.querySelector('#sa-recruitment-input');
        
        let isFieldValid = true;
        
        if (isFileGroup) {
           // Check our manually managed files
           if (this.filesArray.length === 0) isFieldValid = false;
        } else if (isRecruitmentGroup) {
           // Check recruitment hidden input
           const recruitmentInput = field.querySelector('#sa-recruitment-input');
           if (!recruitmentInput.value) isFieldValid = false;
        } else if (input) {
           // Standard check for text/email/tel/select
           const val = input.value.trim();
           
           // Find or create error text element if it doesn't exist
           let errorText = field.querySelector('.sa-job-detail__error-text');
           
           if (!val) {
             isFieldValid = false;
             if (errorText) errorText.textContent = 'This value should not be blank.';
           } else if (input.type === 'email') {
             const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
             if (!emailRegex.test(val)) {
               isFieldValid = false;
               if (errorText) errorText.textContent = 'Please enter a valid email address';
             }
           }
        } else {
            // Optional field or no required input found
            return;
        }

        if (!isFieldValid) {
            isValid = false;
            field.classList.add('sa-job-detail__form-group--error');
            if (!firstInvalid) firstInvalid = field;
        } else {
            field.classList.remove('sa-job-detail__form-group--error');
        }
      });

      if (firstInvalid) {
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Focus if possible (not for file input which is hidden)
        const input = firstInvalid.querySelector('input:not([type="hidden"]), select');
        if (input) input.focus();
        
        return false;
      }

      return isValid;
    }

    async handleSubmit(e) {
      e.preventDefault();
      
      const submitBtn = this.submitBtn;
      if (submitBtn) {
        submitBtn.classList.add('loading');
        submitBtn.disabled = true;
      }

      // Hide previous messages
      if (this.successMessage) this.successMessage.style.display = 'none';

      // Validation
      if (!this.validateForm()) {
        if (submitBtn) {
          submitBtn.classList.remove('loading');
          submitBtn.disabled = false;
          // Restore button state (might need to re-check uploads if validation failed but uploads are ongoing?)
          // But validateForm returns false immediately, and updateSubmitButtonState handles upload state.
          // We should call updateSubmitButtonState() to be safe.
          this.updateSubmitButtonState();
        }
        return;
      }

      // Check for failures (we don't check for pending here because button is disabled if pending)
      const failedFiles = this.filesArray.filter(f => f.status === 'error');

      if (failedFiles.length > 0) {
        alert('Some files failed to upload. Please remove them and try again.');
        if (submitBtn) {
          submitBtn.classList.remove('loading');
          this.updateSubmitButtonState(); // Restore state
        }
        return;
      }

      try {
        // Collect File IDs
        const fileIds = this.filesArray.map(f => f.id).filter(id => id);

        // Step 2: Submit Form
        const formData = new FormData(this.form);
        
        // Merge name
        const firstName = formData.get('first_name');
        const lastName = formData.get('last_name');
        if (firstName && lastName) {
             formData.set('name', `${firstName} ${lastName}`);
        }
        formData.delete('first_name');
        formData.delete('last_name');

        // Remove raw files from form submission
        formData.delete('resume');

        // Add file IDs
        if (fileIds.length > 0) {
            formData.set('file_ids', JSON.stringify(fileIds));
        }

        // Use the new endpoint
        const response = await fetch(`/apps/sharpa-careers/submit?shop=${this.shopDomain}`, {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          this.form.reset();
          this.filesArray = []; // Clear files
          this.updateFileInput(); // Clear input
          this.renderFileList(); // Clear UI
          
          if (this.successMessage) {
            this.successMessage.style.display = 'block';
            this.successMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setTimeout(() => {
               this.successMessage.style.display = 'none';
            }, 5000);
          }
          
          // Reset button to original state
          if (submitBtn) {
            submitBtn.classList.remove('loading');
            submitBtn.disabled = false;
            if (this.originalSubmitBtnContent) {
                submitBtn.innerHTML = this.originalSubmitBtnContent;
            }
          }
        } else {
          const json = await response.json();
          throw new Error(json.error || 'Submission failed');
        }
      } catch (error) {
        console.error('Application submission error:', error);
        alert('Submission failed: ' + error.message);
      } finally {
        if (submitBtn) {
           // Only restore if we are not successful (success block handles reset)
           // Or just ensure loading class is removed
           submitBtn.classList.remove('loading');
           // If successful, button state is already reset.
           // If failed, we need to reset.
           // Let's just rely on updateSubmitButtonState to restore correct state based on files
           if (!this.filesArray.length) {
               // If success cleared files, ensure button is enabled
               submitBtn.disabled = false;
               if (this.originalSubmitBtnContent) submitBtn.innerHTML = this.originalSubmitBtnContent;
           } else {
               // If error, re-evaluate
               this.updateSubmitButtonState();
           }
        }
      }
    }
  }

  customElements.define('sa-job-detail', SaJobDetail);
}
